import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  extractLotProperties,
  implementsMoleculeLotInterface,
  LotProperties,
} from 'notebook/integrations/chemistry/custom-evals/LotPropertiesCustomEval';
import {
  getLotConcentrationStatusFromProperties,
  LotConcentrationStatus,
} from 'notebook/integrations/chemistry/custom-evals/LotPurityConcentrationCustomEval';
import {getPredicate, UnitTypePredicates} from 'notebook/structured-tables/columns/UnitTypePredicates';
import {
  GetLotPropertiesQuery_Query as GetLotPropertiesQueryResponse,
  GetLotPropertiesQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetLotPropertiesQuery';
import {GET_LOT_PROPERTIES_QUERY} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {isCellLoading} from 'notebook/tables/excess/Util';
import {getBioentityIdFromEvaluatedCell} from 'notebook/tables/search-scoping/rowScopedSearch';
import {
  RowScopedUnitTypeFilterArgs,
  RowScopedUnitTypeFilterResult,
} from 'notebook/tables/search-scoping/rowScopedUnitTypeFilter';
import {assertNever} from 'util/Types';

function getLotProperties(data: GetLotPropertiesQueryResponse): LotProperties | null {
  const lot = data.bioentities[0];
  if (lot == null || isInaccessible(lot) || !implementsMoleculeLotInterface(lot)) {
    return null;
  }
  return extractLotProperties(lot.tagsJson);
}

/**
 * Restricts the Amount unit picker to volume units while the row's lot has a concentration.
 *
 * Loads the lot through the same query the lot properties custom eval and the lot row updater use, so a lot the
 * table already fetched is read from the Apollo cache. The picker follows the live lot; in the notebook, the
 * Amount unit custom eval follows the lot properties saved with the entry until the table is re-synced.
 */
export default function useStoichiometryLotAmountUnitTypeFilter({
  sourceCell: lotCell,
}: RowScopedUnitTypeFilterArgs): RowScopedUnitTypeFilterResult {
  const lotId = getBioentityIdFromEvaluatedCell(lotCell ?? undefined);
  const queryResult = useSkippableBenchlingQuery<
    GetLotPropertiesQueryResponse,
    GetLotPropertiesQueryVariables
  >(GET_LOT_PROPERTIES_QUERY, {variables: {lotIds: lotId != null ? [lotId] : []}, skip: lotId == null});
  // A Lot entered as text is still a string until it resolves to an entity, so there's no lot id to query yet.
  if (lotCell != null && isCellLoading(lotCell)) {
    return {loading: true};
  }
  switch (queryResult.type) {
    case QueryResultType.PENDING:
      return {loading: true};
    case QueryResultType.SKIPPED:
    case QueryResultType.ERROR:
      return {loading: false, predicate: null};
    case QueryResultType.SUCCESS: {
      const status = getLotConcentrationStatusFromProperties(getLotProperties(queryResult.data));
      return {
        loading: false,
        predicate:
          status === LotConcentrationStatus.HAS_CONCENTRATION
            ? getPredicate(UnitTypePredicates.VOLUME)
            : null,
      };
    }
    default:
      return assertNever(queryResult);
  }
}
